import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Divider, Grid, Typography } from '@mui/material'
import { formatDate } from 'utils/date-utils'
import { InterestFeeResult } from 'types/delayed-interests/interest-fee-result'

export type FeesForm = {
    endDate: string | Date
}

type PropertyDelayedFeesItemProps = {
    feeResult: InterestFeeResult
}

export const PropertyDelayedFeesItem: React.FC<PropertyDelayedFeesItemProps> = ({ feeResult }) => {
    const { t } = useTranslation()

    return (
        <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="h6" fontWeight={700}>
                {`${t('TEXT_YEAR_TERM')} ${feeResult.invoice.term} ${feeResult.invoice.year}`}
            </Typography>

            <Typography variant="body1" component="span">
                {`${t('TEXT_INVOICE_NR')}: ${feeResult.invoice.invoiceNr}`}
            </Typography>

            <Typography variant="body1" component="span">
                {`${t('TEXT_KID_NR')}: ${feeResult.invoice.kid}`}
            </Typography>

            <Typography variant="body1" component="span" sx={{ mt: 2 }}>
                {`${t('TEXT_ORIGINAL_SUM', 'Original sum')}: ${Intl.NumberFormat('no-NO').format(
                    feeResult.invoice.originalAmount,
                )} NOK`}
            </Typography>

            <Typography variant="body1" component="span">
                {`${t('TEXT_PAYMENT_DUE', 'Payment due')}: ${formatDate(feeResult.invoice.dueDate)}`}
            </Typography>

            <Grid container mt={1} rowSpacing={1}>
                <Grid item xs={2}>
                    {t('TEXT_FROM')}
                </Grid>

                <Grid item xs={3}>
                    {t('TEXT_BALANCE')}
                </Grid>

                <Grid item xs={2}>
                    {t('TEXT_DAYS', 'Days')}
                </Grid>

                <Grid item xs={2}>
                    {t('TEXT_INTEREST_RATE_LABEL', 'Rentesats')}
                </Grid>
                <Grid item xs={3}>{`${t('TEXT_PAYMENT_AMOUNT', 'Amount')} (NOK)`}</Grid>

                {feeResult.periods.map((period) => (
                    <React.Fragment key={`${period.periodStartDate} - ${period.periodEndDate}`}>
                        <Grid item xs={2}>
                            <Typography variant="body2">{formatDate(period.periodStartDate)}</Typography>
                        </Grid>

                        <Grid item xs={3}>
                            <Typography variant="body2">
                                {Intl.NumberFormat('no-NO').format(period.outstandingPrincipalAtPeriodEnd)}
                            </Typography>
                        </Grid>

                        <Grid item xs={2}>
                            <Typography variant="body2">{period.numberOfDays}</Typography>
                        </Grid>

                        <Grid item xs={2}>
                            <Typography variant="body2">
                                {Intl.NumberFormat('no-NO').format(period.annualRateDuringPeriod * 100)} %
                            </Typography>
                        </Grid>

                        <Grid item xs={3}>
                            <Typography variant="body2" fontWeight={700}>
                                {Intl.NumberFormat('no-NO').format(period.interestAccruedInPeriodRounded)}
                            </Typography>
                        </Grid>
                    </React.Fragment>
                ))}
            </Grid>

            <Divider sx={{ my: 2 }} />
        </Box>
    )
}
